import React, { MouseEvent, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { RootState } from '../../../configs/store';
import ToolTip from '../../ToolTip/ToolTip';
import LottoContext from '../Context/Lotto.context';
import Number from './Number/Number';
import './LottoNumberPicker.scss';
import AppModeContext from 'src/containers/AppMode/AppMode.context';

const BONUS_MARKET = 700;
const X_PLUS_BONUS_MARKET = 800;

export default function LottoNumberPicker() {
  const { t } = useTranslation();
  const { RETAIL_MODE } = useContext(AppModeContext);
  const { singleNumberEvent: { numberInfos: { regular, bonus }, markets, marketType } } = useSelector((state: RootState) => state.lotto);
  const { selectedMarket, selectedNumbers, setselectedNumbers, setSelectedMarket, tab, setIsManualSelection, usedNumbers } = useContext(LottoContext);
  const dispatch = useDispatch();

  const showBankers = () => {
    if (!selectedMarket || !selectedMarket.specifiers) return false;
    if (bonus?.sameBallset) {
      return selectedNumbers?.length > +selectedMarket?.specifiers[0].value && +selectedMarket?.specifiers[0].value !== 1;
    } else {
      const filteredSelected = _.filter(selectedNumbers, { type: tab === 700 ? 'star' : 'ball' });
      return filteredSelected.length > +selectedMarket?.specifiers[0]?.value && +selectedMarket?.specifiers[0]?.value !== 1;
    }
  };

  useEffect(() => {
    if (selectedNumbers && _.filter(selectedNumbers, { isBanker: true }).length > 0){
      // Filter Bankers to Match selected marketType and if is same ballset or no
      const selected = JSON.parse(JSON.stringify(selectedNumbers));
      const type = selectedMarket?.typeCode !== BONUS_MARKET || bonus?.sameBallset ? 'ball' : 'star';
      const bankers =_.filter(selected, { type, isBanker: true });
      if (bankers.length >= +selectedMarket?.specifiers[0].value || !showBankers()) {
        // If for some reason we had bankers and we remove a value or change to a market that does not allow bankers we remove all of them
        setselectedNumbers(_.map(selected, (number) => { return { ...number, isBanker:false }; }));
      }
    }
  }, [bonus?.sameBallset, selectedMarket, selectedNumbers, setselectedNumbers]);

  //#region LOGICFUNCTION

  const prevState = useRef([]);
  useEffect(() => {
    if (prevState.current.length > 4){
      prevState.current.shift();
    }
    if (!_.isEqual(selectedNumbers, prevState.current[prevState.current.length - 1])) {
      prevState.current.push(selectedNumbers);
    }
  });

  const handleUndo = (e:MouseEvent) => {
    e.preventDefault();
    prevState.current.pop();
    if (prevState.current.length > 0) {
      setselectedNumbers(JSON.parse(JSON.stringify(prevState.current[prevState.current.length -1])));
    }
  };

  const handleAll = () => {
    if (selectedMarket.typeCode !== X_PLUS_BONUS_MARKET){
      if (RETAIL_MODE){
        setIsManualSelection(true);
      }

      const type = selectedMarket.typeCode === BONUS_MARKET && !bonus.sameBallset ? 'star' : 'ball';
      const numberOfBalls = type==='ball' ? regular.balls : bonus.colors.length;
      const startNumber = type === 'ball' ? regular.startNumber : bonus.startNumber;

      const numbers = Array.apply(null, Array(numberOfBalls - startNumber + 1)).map(function (i, j) {
        return {
          type,
          number: j + startNumber,
          isBanker: false,
          color: regular.colors[j]
        };
      });

      setselectedNumbers(numbers);
    }
  };

  const handleClick = (numberBall:number, type:string) => async (e:MouseEvent) => {
    e.preventDefault();
    if (RETAIL_MODE){
      setIsManualSelection(true);
    }
    if (tab !== selectedMarket.typeCode){
      // we change market if we are in a tab of a market that is not selected and we click a number

      const market =_.find(markets, { typeCode: tab });
      const index = _.findIndex(marketType, { typeCode: market.typeCode });
      market['header'] = marketType[index].label; //we add market header so we do not have to add a new variable to context
      setSelectedMarket(market);
    }
    const numberIndex = _.findIndex(selectedNumbers, { number:numberBall, type });
    if (numberIndex === -1){
      if (selectedMarket.typeCode === X_PLUS_BONUS_MARKET){
        setselectedNumbers(_.sortBy([...selectedNumbers, {
          type,
          number: numberBall,
          isBanker:false,
          color: regular.colors[numberBall -1]
        }], 'type', 'number'));

      } else {
        setselectedNumbers(_.sortBy([...selectedNumbers, {
          type,
          number: numberBall,
          isBanker:false,
          color: regular.colors[numberBall -1]
        }], 'number'));
      }
    } else {
      //If sorting when adding, there is no need to sort on summary or when I make the bakend Call for permutations
      setselectedNumbers(_.filter(selectedNumbers, ({ number, type }) => number !== numberBall && type===type));
    }
  };

  const handleBanker = (numberBall:number, type:string) => async (e:MouseEvent) => {
    e.preventDefault();
    const selected = JSON.parse(JSON.stringify(selectedNumbers));
    const bankers =_.filter(selected, { type, isBanker: true });
    const index = _.findIndex(selected, { number:numberBall, type });
    //Here we add bankers or remove them according to rules, the last return is to avoid a call on combinations.
    if ((bankers.length +1 < +selectedMarket?.specifiers[0].value) && selected[index].isBanker === false) {
      selected[index].isBanker = true;
    } else if (selected[index].isBanker === true){
      selected[index].isBanker = false;
    } else {
      return;
    }
    setselectedNumbers(selected);
  };

  const handleRandom = async (e: MouseEvent) => {
    e.preventDefault();

    if (selectedMarket.typeCode === X_PLUS_BONUS_MARKET){
      let newNumbersArray = [];

      const balls = await dispatch.lotto.getRandomNumber([{
        balls: +selectedMarket.specifiers[0].value,
        from: regular.startNumber,
        to: regular.balls,
        type: 'ball'
      }, {
        balls: +selectedMarket.specifiers[1].value,
        from: bonus.startNumber,
        to: bonus.balls,
        type: 'bonus',
        sameDrum:bonus.sameBallset
      }
      ]);

      newNumbersArray = _.concat(
        newNumbersArray,
        _.map(balls.regularDrum, (numberBall) => {
          numberBall.color = regular.colors[numberBall.number -1];
          return numberBall;
        }),
        _.map(balls.bonusDrum, (numberBall) => {
          numberBall.color = regular.colors[numberBall.number -1];
          return numberBall;
        })
      );

      setselectedNumbers(_.sortBy(newNumbersArray, 'type', 'number'));
    } else {

      const from = selectedMarket.typeCode=== BONUS_MARKET && !bonus.sameBallset ? bonus.startNumber : regular.startNumber;
      const to = selectedMarket.typeCode=== BONUS_MARKET && !bonus.sameBallset ? bonus.balls : regular.balls;
      const type = selectedMarket.typeCode=== BONUS_MARKET && !bonus.sameBallset ? 'star' : 'ball';
      const balls = 5;

      const numbers = await dispatch.lotto.getRandomNumber({ balls, from, to, type });
      const newNumbers = _.map(numbers, (numberBall) => {
        numberBall.color = regular.colors[numberBall.number -1];
        return numberBall;
      });
      setselectedNumbers(_.sortBy(newNumbers, 'number'));
    }
  };
  //#endregion

  const generateArray = (min:number, max:number) => {
    return Array.apply(null, Array(max - min + 1)).map(function (i, j) {
      return j + min;
    });
  };

  return (
    <div className="number-picker">
      <div className="number-picker__info">
        <ToolTip
          number={ 3 }
          title={ t('numbers.pickNumber') }
          message={ t('numbers.toolTip3') }
          className="number-picker__number"
        />
        <button
          className={
            classNames({
              'number-picker__clear': true,
              'number-picker__random--hidden': usedNumbers.length === 0
            })
          }
          onClick={ () => setselectedNumbers(usedNumbers) }
        > { t('numbers.reuseNumbers') } </button>
        <button className="number-picker__random" onClick={ handleAll }> { t('numbers.all') } </button>
        <button
          className={
            classNames({
              'number-picker__undo': true,
              'number-picker__undo--disabled' : prevState.current.length < 1 || selectedNumbers.length < 1
            })
          }
          onClick= { handleUndo }
          disabled={ prevState.current.length < 1 || selectedNumbers.length < 1 }
        > { t('numbers.undo') } </button>
        <button
          className="number-picker__clear"
          onClick={ () => setselectedNumbers([]) }
          disabled={ selectedNumbers.length < 1 }
        > { t('numbers.clearAll') }</button>
        <button className="number-picker__random" onClick={ handleRandom }> { t('numbers.random') } </button>
      </div>
      <>
        {
          tab === X_PLUS_BONUS_MARKET && (
            <div className="number-picker__drum-title">{ t('numbers.mainDrum') }</div>
          )
        }
        <div className="number-picker__balls">
          {
            (regular && (bonus?.sameBallset || tab !== BONUS_MARKET)) && (
              _.map(generateArray(regular?.startNumber, regular?.balls), (ball, index) => {
                return (<Number
                  key={ index }
                  type="ball"
                  color={ regular.colors[ball -1]?.toLowerCase() || 'b' }
                  value={ +index + 1 }
                  bankers = { false }
                  alreadySelected = {
                    bonus?.sameBallset && tab === X_PLUS_BONUS_MARKET ?
                      _.findIndex(selectedNumbers, { number: +index+1, type: 'bonus' }) > -1 :
                      false
                  }
                  isActive={ _.findIndex(selectedNumbers, { number: +index+1, type: 'ball' }) > -1 }
                  onClick = { handleClick }
                />);
              }
              )
            )
          }
          {
            bonus?.sameBallset === false && tab===BONUS_MARKET && (
              _.map(generateArray(bonus?.startNumber, bonus?.balls), (ball, index) => (
                <Number
                  key={ index }
                  type="star"
                  color={ regular.colors[ball -1]?.toLowerCase() || 'b' }
                  value={ +index + 1 }
                  bankers = { false }
                  isActive={ _.findIndex(selectedNumbers, { number: +index+1, type: 'star' }) > -1 }
                  onClick = { handleClick }
                />
              ))
            )
          }
        </div>
      </>
      {
        tab === X_PLUS_BONUS_MARKET && (
          <>
            <div className="number-picker__drum-title">{ t('numbers.bonusDrum') }</div>
            <div className="number-picker__balls">
              {
                bonus?.sameBallset && (
                  _.map(generateArray(regular?.startNumber, regular?.balls), (ball, index) => (
                    <Number
                      key={ index }
                      type="bonus"
                      color={ regular.colors[ball -1]?.toLowerCase() || 'b' }
                      value={ +index + 1 }
                      bankers = { false }
                      isActive={ _.findIndex(selectedNumbers, { number: +index+1, type: 'bonus' }) > -1 }
                      alreadySelected = { _.findIndex(selectedNumbers, { number: +index+1, type: 'ball' }) > -1 }
                      onClick = { handleClick }
                      sameBallset = { true }
                    />
                  ))
                )
              }
              {
                !bonus?.sameBallset && (
                  _.map(generateArray(bonus?.startNumber, bonus?.balls), (ball, index) => (
                    <Number
                      key={ index }
                      type="bonus"
                      color={ regular.colors[ball -1]?.toLowerCase() || 'b' }
                      value={ +index + 1 }
                      bankers = { false }
                      isActive={ _.findIndex(selectedNumbers, { number: +index+1, type: 'bonus' }) > -1 }
                      onClick = { handleClick }
                      sameBallset = { false }
                    />
                  ))
                )
              }
            </div>
          </>
        )
      }
      <div className={
        classNames({
          'number-picker__bankers': true,
          'number-picker__bankers--hidden': showBankers || tab === X_PLUS_BONUS_MARKET
        })
      }
      >
        {
          showBankers() && tab !== X_PLUS_BONUS_MARKET && (
            <>
              <div className="number-picker__banker-title">
                { t('numbers.bankers') }
              </div>
              <div className="number-picker__banker-balls">
                {
                  _.map(_.filter(_.sortBy(selectedNumbers, 'number'),
                    { type: (bonus?.sameBallset === false && tab=== BONUS_MARKET)? 'star' : 'ball' }), (ball, index) => {
                    const type = tab === 700 && bonus?.sameBallset === false ? 'star' : 'ball';
                    return (
                      <Number
                        key={ index }
                        type={ type }
                        color={ regular.colors[ball.number -1].toLowerCase() }
                        value={ ball.number }
                        bankers ={ true }
                        isBanker = { _.findIndex(selectedNumbers, { number:ball.number, isBanker: true, type }) !== -1 }
                        isActive={ false }
                        onClick = { handleBanker }
                      />
                    );
                  })
                }
              </div>
            </>
          )
        }
      </div>
    </div>
  );
}
